/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { Input } from 'reactstrap';
import ValueSliderGroupRow from './ValueSliderGroupRow';
import { BaseInputProps, HandleValueChangeProps } from './InputProps';
import { findAddon } from './addonUtils';
import UniversalTrackerClassInterface from '../../../../model/UniversalTrackerClassInterface';
import { Addons } from '@g17eco/types/questionInterfaces';

export interface SliderInterface {
  code: string,
  name: string,
  value?: number,
  description?: string,
  icon?: string,
  tooltip?: any
}

const sliderAccumulator = (acc: number, slider: any) => {
  if (!slider.value || slider.value === undefined || isNaN(slider.value) || String(slider.value) === '') {
    return acc;
  }
  return acc + slider.value;
};

interface SliderChangeProps {
  sliders: SliderInterface[]
  code: string
  value: any;
  min?: number;
  max?: number;
  handleChange: (sliders: SliderInterface[]) => void;
}

const handleSliderChange = ({ sliders, code, value, min = 0, max = 100, handleChange }: SliderChangeProps) => {
  const currentSlider = sliders.find(slider => slider.code === code);
  if (!currentSlider || min === undefined || max === undefined) {
    return;
  }

  if (isNaN(value) || value === '') {
    currentSlider.value = undefined;
  } else {
    currentSlider.value = Math.min(max, Math.max(value, min));
  }

  const sliderSum = sliders.reduce(sliderAccumulator, 0);
  let remainingSliders = sliders.filter(slider => slider.code !== code);

  let overflow = sliderSum - max;
  if (overflow > 0) {
    if (value % 2 === 0) { // This emulates simplistic alternate incrementing
      remainingSliders = remainingSliders.reverse();
    }

    while (overflow > 0) {
      let hasRemainingVC = false;
      for (let i = 0; i < remainingSliders.length; i++) {
        const slider = remainingSliders[i];
        if (overflow > 1 && slider.value !== undefined && slider.value > 1) {
          hasRemainingVC = true;
          slider.value -= 1;
          overflow -= 1;
        }
      }
      if (!hasRemainingVC) {
        // Nowhere else to distribute, so cap it. This shouldn't happen as it suggest >100
        if (currentSlider.value !== undefined) {
          currentSlider.value -= overflow;
        }
        overflow = 0;
      }
    }
  }

  handleChange(sliders);
}

export default function ValueSliderGroup(props: {
  sliders: SliderInterface[],
  handleChange: (sliders: SliderInterface[]) => void,
  universalTracker: UniversalTrackerClassInterface,
  addons: Addons | undefined;
  warningMessage?: JSX.Element;
} & Pick<BaseInputProps, 'numberScale' | 'handleNumberScaleChange' | 'status' | 'initiativeUtr'>) {
  const { sliders, handleChange, universalTracker, addons, numberScale, handleNumberScaleChange } = props;
  const sliderSum = sliders.reduce(sliderAccumulator, 0);
  const max = universalTracker.getMax() || 100;

  return (
    <>
      {sliders.map((slider) => {
        const { beforeAddon, afterAddon } = findAddon(addons, slider.code);
        return (
          <div className='position-relative row' key={`slider-slider-${slider.code}`}>
            {beforeAddon?.element || null}
            <div className='col'>
              <ValueSliderGroupRow
                code={slider.code}
                name={slider.name}
                value={slider.value}
                description={slider.description}
                icon={slider.icon}
                tooltip={slider.tooltip}
                universalTracker={universalTracker}
                handleChange={({ value, min, max }: HandleValueChangeProps) =>
                  handleSliderChange({ sliders, code: slider.code, value, min, max, handleChange })
                }
                numberScale={numberScale}
                handleNumberScaleChange={handleNumberScaleChange}
                warningMessage={props.warningMessage}
                initiativeUtr={props.initiativeUtr}
                status={props.status}
              />
            </div>
            {afterAddon?.element || null}
          </div>
        );
      })}
      <div className='row'>
        <div className='col-6' />
        <div className='col-6'>
          <hr />
        </div>
      </div>
      <div className='row slidergroup-total g-0'>
        <div className='col-6'></div>
        <div className='col-3 pl-3'>
          <div className='ml-2 mb-1'>Total</div>
          <Input placeholder={`${sliderSum}%`} type='number' disabled={true} className='w-100 styled-readonly' />
        </div>
        <div className='col-3 pl-2'>
          <div className='ml-2 mb-1'>Remaining!</div>
          <Input
            placeholder={`${max - sliderSum}%`}
            type='number'
            disabled={true}
            className={`w-100 styled-readonly ${sliderSum > max ? 'error' : sliderSum === max ? 'success' : ''}`}
          />
        </div>
      </div>
    </>
  );
}
